import { Router } from "@frui.ts/screens";
import { watchBusy } from "@frui.ts/screens/dist";
import type { EntityNames } from "config";
import { Modules } from "config";
import ChargeResponse from "entities/chargeResponse";
import ChargeOrderStatus from "entities/chargeOrderStatus";
import type RefundDisplayStatus from "entities/refundDisplayStatus";
import ChargeDisplayStatus from "entities/chargeDisplayStatus";
import type PaymentType from "entities/paymentType";
import type SourceType from "entities/sourceType";
import type LicensePlate from "entities/licensePlate";
import { action, computed } from "mobx";
import ChargesRepository from "repositories/chargesRepository";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import EventBus, {
  callCenterViewModelName,
  chargeFuelTypeChanged,
  chargeRefundCandidateCreated,
  emailChanged,
  totalCountCharges,
  totalCountExemptions,
  totalCountMessages,
  totalCountVehicleRegister,
  vehicleSearchStatus,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import CallCenterListViewModel from "../callCenterListViewModel";
import type ChangeEmailViewModel from "./changeEmailViewModel";
import type ChangeLicensePlateViewModel from "./changeLicensePlateViewModel";
import type ChargeDetailViewModel from "./chargeDetail/chargeDetailViewModel";
import type FuelTypeChangeRefundViewModel from "./fuelTypeChangeRefundViewModel";
import type RefundChargeViewModel from "./refundChargeViewModel";
import camelCase from "lodash/camelCase";
import RefundDisplayType from "../../../../entities/refundDisplayType";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import ChargeConfirmationDialogViewModel from "viewModels/callCenter/actions/chargeConfirmationDialogViewModel";
import ModalService from "services/modalService";
import { chargeOrderStatusToVariant } from "entities/chargeOrderStatus";
import Charge from "entities/chargeResponse";
import PriceListService from "services/priceListService";
import { formatAmount } from "utils/localizations";
import type SearchChargesRequest from "entities/searchChargesRequest";
import ExemptionRepository from "repositories/exemptionRepository";
import { trimObjectStrings } from "utils/filters";
import EmailMessageRepository from "repositories/emailMessageRepository";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import CallCenterModelNamesEnum from "entities/callCenterModelNamesEnum";

export const chargesNavigationName = CallCenterModelNamesEnum.Charges;

export enum ChargeAction {
  CHARGE_DETAIL_ACCESS = "ChargeDetailAccess",
  REFUNDATION = "Refundation",
  FUEL_TYPE_CHANGE = "FuelTypeChange",
  VEHICLE_REGISTRATION_CHANGE = "VehicleRegistrationChange",
  EMAIL_ADDRESS_CHANGE = "EmailAddressChange",
  RECEIPT_SENDING = "ReceiptSending",
  ANONYMIZED_RECEIPT_DOWNLOAD = "AnonymizedReceiptDownload",
}

export enum ChargesRowColor {
  RED = "row-red",
  WHITE = "row-white",
  GREEN = "row-green",
}

@Router.registerRoute({ route: chargesNavigationName, name: Router.Self })
export default class ChargesViewModel extends CallCenterListViewModel<ChargeResponse, SearchChargesRequest> {
  protected readonly modelName: EntityNames = "charge";

  constructor(
    protected eventBus: EventBus,
    protected localizationService: LocalizationService,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    protected securityService: SecurityService,
    protected repository: ChargesRepository,
    protected exemptionRepository: ExemptionRepository,
    protected emailMessageRepository: EmailMessageRepository,
    protected vehicleRegisterRepository: VehicleRegisterRepository,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    private modalService: ModalService,
    private changeLicensePlateVMFactory: ReturnType<typeof ChangeLicensePlateViewModel.Factory>,
    private refundChargeVMFactory: ReturnType<typeof RefundChargeViewModel.Factory>,
    private fuelTypeChangeRefundVMFactory: ReturnType<typeof FuelTypeChangeRefundViewModel.Factory>,
    private changeEmailVMFactory: ReturnType<typeof ChangeEmailViewModel.Factory>,
    private chargeDetailVMFactory: ReturnType<typeof ChargeDetailViewModel.Factory>
  ) {
    super();
    this.navigationName = chargesNavigationName;
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      this.eventBus.subscribe(emailChanged, this.onEmailChanged),
      this.eventBus.subscribe(chargeRefundCandidateCreated, this.onChargeRefundCandidateCreated),
      this.eventBus.subscribe(chargeFuelTypeChanged, this.onChargeFuelTypeChanged)
    );
    return super.onInitialize();
  }

  protected onActivate() {
    this.eventBus.publish(callCenterViewModelName({ callCenterViewModelName: CallCenterModelNamesEnum.Charges }));

    return super.onActivate();
  }

  @computed
  get licensePlate(): LicensePlate | undefined {
    if (this.appliedFilter?.licensePlate || this.appliedFilter?.countryId) {
      return {
        licensePlate: this.appliedFilter.licensePlate ?? "",
        countryId: this.appliedFilter.countryId ?? "",
      };
    }

    return undefined;
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "charges.name");
  }

  @computed
  get actions() {
    return Object.values(ChargeAction).filter(action => this.allowToAction(action));
  }

  @computed
  get allowVehicleRehisterSearch() {
    return this.securityService.allowTo("callcenter");
  }

  @action.bound
  chargeOrderRowColor(orderStatus: ChargeOrderStatus): string {
    switch (orderStatus) {
      case ChargeOrderStatus.Canceled:
        return ChargesRowColor.RED;

      case ChargeOrderStatus.Open:
        return ChargesRowColor.WHITE;

      case ChargeOrderStatus.Paid:
        return ChargesRowColor.GREEN;
    }
  }

  @action.bound
  rowColor(orderState: ChargeOrderStatus, chargeDisplayStatus: ChargeDisplayStatus) {
    const color = this.chargeOrderRowColor(orderState);
    switch (color) {
      case ChargesRowColor.WHITE: {
        return "row-white";
      }
      case ChargesRowColor.RED:
        return "row-red";
      case ChargesRowColor.GREEN: {
        if (chargeDisplayStatus === ChargeDisplayStatus.BeforeValidity || chargeDisplayStatus === ChargeDisplayStatus.Valid) {
          return "row-green";
        }
        if (
          chargeDisplayStatus === ChargeDisplayStatus.RefundedToCustomer ||
          chargeDisplayStatus === ChargeDisplayStatus.RefundedToDistributor
        ) {
          return "row-blue";
        }
        if (chargeDisplayStatus === ChargeDisplayStatus.AfterValidity) {
          return "row-darkgray";
        }
        return "row-white";
      }
    }
    return chargeOrderStatusToVariant(orderState);
  }

  private prepareFilterChargeId() {
    //EDAZ-5810 fix paging must be undefined
    //EDAZ-11810 filter by chargeId must not be undefined
    if (this.pagingFilter.offset > 0) {
      this.appliedFilter.chargeId = undefined;
    }
  }

  @action.bound
  protected async loadItems() {
    if (this.appliedFilter.orderIdent) {
      this.appliedFilter.orderIdent = parseInt(String(this.appliedFilter.orderIdent).trim());
    }
    this.appliedFilter = trimObjectStrings(this.appliedFilter);
    this.prepareFilterChargeId();

    const result = await this.repository.getAll(this.appliedFilter, this.pagingFilter, this.abortController?.signal);
    this.publishTotalCount(result.metadata.total);
    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @action.bound
  protected async loadRelatedCounts() {
    const { licensePlate, officialRecordId, countryId, orderIdent, chargeId, chargeOrderId } = this.appliedFilter;
    if (licensePlate || officialRecordId || (countryId && officialRecordId) || (countryId && licensePlate)) {
      const filter = {
        licensePlate: licensePlate,
        officialRecordId: officialRecordId,
        countryId: countryId,
      };
      const { totalCount } = await this.exemptionRepository.totalCount(filter);
      this.eventBus.publish(totalCountExemptions({ totalCountExemptions: totalCount }));
    } else {
      this.eventBus.publish(totalCountExemptions({ totalCountExemptions: 0 }));
    }

    if (
      (countryId && (chargeId || licensePlate || orderIdent || chargeOrderId)) ||
      chargeId ||
      licensePlate ||
      orderIdent ||
      chargeOrderId
    ) {
      const { totalCount } = await this.emailMessageRepository.totalCount(this.appliedFilter);
      this.eventBus.publish(totalCountMessages({ totalCountMessages: totalCount }));
    } else {
      this.eventBus.publish(totalCountMessages({ totalCountMessages: 0 }));
    }

    if (this.allowVehicleRehisterSearch && (licensePlate || (countryId && licensePlate))) {
      const { totalCount, searchStatus } = await this.vehicleRegisterRepository.totalCount(this.appliedFilter);
      this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: totalCount }));
      this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: searchStatus }));
    } else {
      this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: 0 }));
      this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
    }
  }

  @action.bound
  protected publishTotalCount(totalCount: number) {
    this.eventBus.publish(totalCountCharges({ totalCountCharges: totalCount }));
  }

  @action.bound
  protected resetRelatedCounts() {
    this.eventBus.publish(totalCountExemptions({ totalCountExemptions: 0 }));
    this.eventBus.publish(totalCountMessages({ totalCountMessages: 0 }));
    this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: 0 }));
    this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
  }

  @action.bound
  allowToAction(action: ChargeAction) {
    switch (action) {
      case ChargeAction.CHARGE_DETAIL_ACCESS:
        return (
          this.securityService.allowTo("callcenter", "read") || this.securityService.allowTo("enforcement.callcenter", "read")
        );

      case ChargeAction.RECEIPT_SENDING:
      case ChargeAction.ANONYMIZED_RECEIPT_DOWNLOAD:
        return this.securityService.allowTo("callcenter", "read");
      case ChargeAction.VEHICLE_REGISTRATION_CHANGE:
        return this.securityService.allowTo("callcenter.chargeauditing", "write");
      case ChargeAction.EMAIL_ADDRESS_CHANGE:
      case ChargeAction.REFUNDATION:
      case ChargeAction.FUEL_TYPE_CHANGE:
      default:
        return this.securityService.allowTo("callcenter", "write");
    }
  }

  @action.bound
  allowToReceiptSending(item: ChargeResponse) {
    return item.permittedActions.receiptSending.isAllowed;
  }

  @action.bound
  allowToRefundation(item: ChargeResponse) {
    return item.permittedActions.refundation.isAllowed;
  }

  @action.bound
  allowToFuelTypeChange(item: ChargeResponse) {
    return item.permittedActions.partialRefundation.isAllowed || item.permittedActions.fuelTypeChange.isAllowed;
  }

  @action.bound
  allowToEmailAddressChange(item: ChargeResponse) {
    return item.permittedActions.emailAddressChange.isAllowed;
  }

  @action.bound
  allowToVehicleRegistrationChange(item: ChargeResponse) {
    return item.permittedActions.vehicleRegistrationChange.isAllowed;
  }

  @action.bound
  allowToChargeDetailAccess(item: ChargeResponse) {
    return item.permittedActions.chargeDetailAccess.isAllowed;
  }

  @action.bound
  allowToAnonymizedReceiptDownload(item: ChargeResponse) {
    return item.permittedActions.anonymizedReceiptDownload.isAllowed;
  }

  @action.bound
  onVehicleRegistrationChangeClick(charge: ChargeResponse) {
    if (this.allowToVehicleRegistrationChange(charge) && this.allowToAction(ChargeAction.VEHICLE_REGISTRATION_CHANGE)) {
      this.openItem = charge;
      this.tryActivateChild(this.changeLicensePlateVMFactory(charge));
    }
  }

  @action.bound
  onRefundationClick(item: ChargeResponse) {
    if (this.allowToRefundation(item) && this.allowToAction(ChargeAction.REFUNDATION)) {
      const dialog = this.refundChargeVMFactory(item, this.appliedFilter);
      this.openItem = item;
      this.tryActivateChild(dialog);
    }
  }

  @action.bound
  onFuelTypeChangeClick(item: ChargeResponse) {
    if (this.allowToFuelTypeChange(item) && this.allowToAction(ChargeAction.FUEL_TYPE_CHANGE)) {
      this.openItem = item;
      this.tryActivateChild(this.fuelTypeChangeRefundVMFactory(item, this.appliedFilter));
    }
  }

  @action.bound
  onEmailAddressChangeClick(item: ChargeResponse) {
    if (this.allowToEmailAddressChange(item) && this.allowToAction(ChargeAction.EMAIL_ADDRESS_CHANGE)) {
      const dialog = this.changeEmailVMFactory(item, this.appliedFilter);
      this.openItem = item;
      this.tryActivateChild(dialog);
    }
  }

  @action.bound
  onChargeDetailAccessClick(item: ChargeResponse) {
    if (this.allowToChargeDetailAccess(item) && this.allowToAction(ChargeAction.CHARGE_DETAIL_ACCESS)) {
      const dialog = this.chargeDetailVMFactory(item);
      this.openItem = item;
      this.tryActivateChild(dialog);
    }
  }

  @action.bound
  async onReceiptSendingClick(item: ChargeResponse) {
    const viewModel = new ChargeConfirmationDialogViewModel(item, this.localizationService);
    await this.modalService.tryActivateChild(viewModel);

    const confirmationResult = await viewModel.showConfirmation();
    this.modalService.closeChild(viewModel);

    if (confirmationResult.confirmed) {
      try {
        await this.busyWatcher.watch(this.repository.sendPaymentConfirmation(item, confirmationResult.forceSingleChargeReceipt));
        this.notificationService.add({
          title: this.translate("sendPaymentConfirmation.sent").replace("%email%", item.emailAddress ?? ""),
          type: NotificationType.SUCCESS,
        });
      } catch (e) {}
    }
  }

  @action.bound
  @watchBusy
  async onAnonymizedReceiptDownloadClick(item: ChargeResponse) {
    if (this.allowToAnonymizedReceiptDownload(item) && this.allowToAction(ChargeAction.ANONYMIZED_RECEIPT_DOWNLOAD)) {
      try {
        const fileResponse = await this.repository.downloadAnonymizedReceipt(item.chargeId);
        download(fileResponse.data, `${item.chargeId}.pdf`, "application/pdf");
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }

  @action.bound
  private onEmailChanged(event: ReturnType<typeof emailChanged>) {
    this.onChargeChanged(event.payload.entity);
  }

  @action.bound
  private onChargeRefundCandidateCreated(event: ReturnType<typeof chargeRefundCandidateCreated>) {
    this.onChargeChanged(event.payload.entity);
  }

  @action.bound
  private onChargeFuelTypeChanged(event: ReturnType<typeof chargeFuelTypeChanged>) {
    this.onChargeChanged(event.payload.entity);
  }

  @action.bound
  private priceListItem(charge: Charge) {
    return this.priceListService.priceListItemById(charge.priceListItemId);
  }

  @action.bound
  translateFuelType(charge: Charge) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === priceItem.fuelTypeId);
    if (!fuelType) {
      return;
    }
    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  @action.bound
  translateChargeType(charge: Charge) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const chargeType = this.enumService.values("charge_types").find(chargeType => chargeType.id === priceItem.chargeTypeId);
    if (!chargeType) {
      return;
    }
    return this.localizationService.translateEnum("chargeValidity", chargeType.validFor);
  }

  @action.bound
  translatePrice(charge: Charge) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }
    return formatAmount(priceItem.price);
  }

  private onChargeChanged(chargeResponse: ChargeResponse) {
    const index = this.items.findIndex(charge => charge.chargeId === chargeResponse.chargeId);

    if (~index) {
      this.items[index] = this.enumService.proxyObjectEnums(chargeResponse);
    }
  }

  disabledActionTooltip(action: ChargeAction, charge: ChargeResponse) {
    if (action === ChargeAction.RECEIPT_SENDING && charge.permittedActions.receiptSending.isEmailAddressMissing) {
      return this.translate(`actions.${camelCase(action)}.tooltipEmailMissing`);
    } else {
      return this.translate(`actions.${camelCase(action)}.tooltip`);
    }
  }

  translatePurchased(chargeResponse: ChargeResponse) {
    let purchased = this.translateSourceType(chargeResponse.orderSourceType);

    if (chargeResponse.paymentType) {
      purchased += ` - ${this.translatePaymentType(chargeResponse.paymentType).replace("Platba ", "")}`;
    }

    return purchased;
  }

  translateSourceType(sourceType: SourceType) {
    return this.localizationService.translateEnum("sourceType", sourceType);
  }

  translatePaymentType(paymentType: PaymentType) {
    return this.localizationService.translateEnum("paymentType", paymentType);
  }

  translateChargeOrderStatus(chargeOrderStatus: ChargeOrderStatus) {
    return this.localizationService.translateEnum("chargeOrderStatus", chargeOrderStatus);
  }

  translateChargeDisplayStatus(chargeDisplayStatus: ChargeDisplayStatus) {
    return this.localizationService.translateEnum("chargeDisplayStatus", chargeDisplayStatus);
  }

  translateChargeRefundDisplayStatus(refundDisplayStatus: RefundDisplayStatus, isPartiallyRefunded: boolean) {
    const prefix = this.localizationService.translateEnum("refundDisplayType", RefundDisplayType.CustomerPartial);
    const statusText = this.localizationService.translateEnum("refundDisplayStatus", refundDisplayStatus);

    if (isPartiallyRefunded) {
      return `${prefix} – ${statusText.toLowerCase()}`;
    } else {
      return statusText;
    }
  }

  translateSubAttribute(model: "licensePlate", attribute: keyof LicensePlate) {
    return this.localizationService.translateAttribute(model, attribute);
  }
}
